/* eslint-disable import/no-named-as-default-member */
/* eslint-disable prettier/prettier */
import CloseIcon from '@mui/icons-material/Close';
import {
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';

import { DateFormat } from '@bvi/date-utils';

import { Image } from '../Image';
import { calendar } from '../icons';

import { styles } from './styles';
import { IFormDatePickerProperties } from './types';

dayjs.extend(utc);


export const FormDatePicker = (properties: IFormDatePickerProperties) => {
  const {
    field: name,
    label,
    fullWidth,
    placeholder,
    error,
    helperText,
    variant = 'outlined',
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    valueFormat = 'utc',
    clearable,
    ...restProperties
  } = properties;

  const { control, resetField } = useFormContext();
  const { field } = useController({
    name,
    control,
  });

  const value = field.value ? dayjs(field.value).startOf("day") : null;
  const inputId = `formDatePickerLabel_${name}`;
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const onChange = (date: Dayjs | null) => {
    if (!date) {
      field.onChange(null);
      return;
    }
    // Only update the form value if the date is valid
    // This prevents updating with partial/invalid input (e.g., just "04" for month)
    if (date.isValid()) {
      const formattedValue = date.toDate(); // Convert to Date object
      field.onChange(formattedValue);
    }
    // If invalid, don't update the form - let the user continue typing
  };
  
  const handleResetField = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    
    resetField(name);
  };

  const textFieldProperties = {
    id: inputId,
    fullWidth,
    error,
    variant,
    placeholder,
    sx: {
      ...styles.textField,
      ...(value && styles.textFieldWithClearButton),
    },
  };

  return (
    <FormControl fullWidth={fullWidth}>
      {label && (
        <InputLabel variant={variant} error={Boolean(error)} htmlFor={inputId}>
          {label}
        </InputLabel>
      )}
      <DatePicker
        {...restProperties}
        format={DateFormat.default}
        open={isPickerOpen}
        // We control `open` to prevent auto-open-on-focus differences across browsers.
        // Opening is handled via the calendar icon button below.
        onClose={() => setIsPickerOpen(false)}
        value={value}
        onChange={onChange}
        slots={{
          openPickerIcon: () => <Image src={calendar} />,
          clearButton: () => (
            <IconButton
              size="small"
              sx={styles.clearButton}
              onClick={handleResetField}
            >
              <CloseIcon sx={styles.clearIcon} />
            </IconButton>
          ),
        }}
       slotProps={{
          openPickerButton: {
            sx: styles.openPickerButton,
            onClick: () => {
              // Ensure we explicitly open the picker via the icon click.
              setIsPickerOpen(true);
            },
          },
          field: { 
            clearable, 
            onClear: handleResetField,
            format: DateFormat.default,
          },
          textField: {
            ...textFieldProperties,
            onBlur: field.onBlur,
          },
        }}
      />
      {helperText && (
        <FormHelperText error={error}>{helperText}</FormHelperText>
      )}
          </FormControl>
  );
};
